import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { getSupabase, USE_MOCKS } from './supabase';
import { DEFAULT_ROLE, isDashboardRole, normalizeRole, type DashboardRole } from '../../../src/services/team/roles';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** The signed-in user's dashboard role (null until loaded or when signed out). */
  role: DashboardRole | null;
  /** True when an admin has turned this user's access off. */
  deactivated: boolean;
  signIn(email: string, password: string): Promise<{ error?: string }>;
  signOut(): Promise<void>;
  displayName: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// In mocks mode we fake a signed-in user so the dashboard is browsable without
// configuring Supabase. VITE_MOCK_ROLE (admin | generator | pm | viewer)
// previews the dashboard as that role; it defaults to admin.
// Flip VITE_USE_MOCKS=false to use real auth.
const MOCK_ROLE: DashboardRole = isDashboardRole(import.meta.env.VITE_MOCK_ROLE)
  ? import.meta.env.VITE_MOCK_ROLE
  : 'admin';

const MOCK_USER = {
  id: 'u4',
  email: 'matt.coletti@ariel.com',
  user_metadata: { full_name: 'Matt Coletti' },
  app_metadata: {},
  aud: 'authenticated',
  created_at: '2026-01-01T00:00:00Z',
} as unknown as User;

/** The user_roles row as read for `userId`. Stale until userId matches the signed-in user. */
interface Access {
  userId: string | null;
  role: DashboardRole | null;
  deactivated: boolean;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(USE_MOCKS ? MOCK_USER : null);
  const [loading, setLoading] = useState(!USE_MOCKS);
  const [access, setAccess] = useState<Access>(
    USE_MOCKS ? { userId: MOCK_USER.id, role: MOCK_ROLE, deactivated: false } : { userId: null, role: null, deactivated: false },
  );

  useEffect(() => {
    if (USE_MOCKS) return;
    const supabase = getSupabase();
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setUser(data.session?.user ?? null);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  // Read the user's own user_roles row (RLS allows reading your own row).
  const userId = user?.id ?? null;
  useEffect(() => {
    if (USE_MOCKS) return;
    if (!userId) {
      setAccess({ userId: null, role: null, deactivated: false });
      return;
    }
    let cancelled = false;
    getSupabase()
      .from('user_roles')
      .select('role, deactivated_at')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) {
          // Fall back to the server default; the API still enforces the real role.
          console.warn('[auth] could not read user role', error.message);
          setAccess({ userId, role: DEFAULT_ROLE, deactivated: false });
          return;
        }
        setAccess({
          userId,
          role: normalizeRole(data?.role),
          deactivated: Boolean(data?.deactivated_at),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Compare ids rather than keeping a "loaded" flag: on the render where the
  // user first appears, the role effect has not run yet, and a flag left over
  // from the signed-out state would let AuthGuard act on a null role (and
  // bounce an admin off /settings/team).
  const accessReady = Boolean(user) && access.userId === user?.id;

  const value = useMemo<AuthContextValue>(() => ({
    user,
    session,
    loading: loading || (Boolean(user) && !accessReady),
    role: accessReady ? access.role : null,
    deactivated: accessReady && access.deactivated,
    displayName: (user?.user_metadata?.full_name as string | undefined) ?? user?.email ?? 'Guest',
    async signIn(email, password) {
      if (USE_MOCKS) {
        setUser(MOCK_USER);
        return {};
      }
      const { error } = await getSupabase().auth.signInWithPassword({ email, password });
      return error ? { error: error.message } : {};
    },
    async signOut() {
      if (USE_MOCKS) {
        setUser(null);
        return;
      }
      await getSupabase().auth.signOut();
    },
  }), [user, session, loading, access, accessReady]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
